import React, { useState, useMemo } from 'react'
import type { Task, TaskStatus } from '../../types/mockup'

interface TimelineViewProps {
  tasks: Task[]
  currentTime: Date
  onTaskClick?: (task: Task) => void
  onTaskStatusChange?: (taskId: string, status: TaskStatus) => void
}

export const TimelineView: React.FC<TimelineViewProps> = ({ 
  tasks, 
  currentTime,
  onTaskClick,
  onTaskStatusChange
}) => {
  const [hoveredTask, setHoveredTask] = useState<string | null>(null)
  const [selectedTask, setSelectedTask] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all')

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ' ' + mins + 'm' : ''}`
    }
    return `${mins}m`
  }

  const getStatusConfig = (status: TaskStatus) => {
    const configs = {
      complete: {
        label: 'Complete',
        color: '#10b981',
        bgColor: 'rgba(16, 185, 129, 0.1)',
        icon: '✓',
        action: null
      },
      in_progress: {
        label: 'In Progress',
        color: '#f59e0b', 
        bgColor: 'rgba(245, 158, 11, 0.1)',
        icon: '●',
        action: 'Mark Done'
      },
      delayed: {
        label: 'Delayed',
        color: '#ef4444',
        bgColor: 'rgba(239, 68, 68, 0.1)',
        icon: '!',
        action: 'Resume'
      },
      at_risk: {
        label: 'At Risk',
        color: '#f97316',
        bgColor: 'rgba(249, 115, 22, 0.1)',
        icon: '⚠',
        action: 'Mark Done'
      },
      pending: {
        label: 'Pending',
        color: '#6b7280',
        bgColor: 'rgba(107, 114, 128, 0.05)',
        icon: '○',
        action: 'Start Task'
      }
    }
    return configs[status] || configs.pending
  }

  const getTaskTiming = (task: Task) => {
    const now = currentTime.getTime()
    const start = task.startTime.getTime()
    const end = task.endTime.getTime()
    const minutesUntilStart = Math.round((start - now) / (1000 * 60))
    const minutesUntilEnd = Math.round((end - now) / (1000 * 60))
    
    if (now >= start && now <= end) {
      return { status: 'active', label: 'Happening Now', urgent: true }
    } else if (minutesUntilStart > 0 && minutesUntilStart <= 15) {
      return { status: 'soon', label: `Starting in ${minutesUntilStart}m`, urgent: true }
    } else if (minutesUntilStart > 0) {
      return { status: 'future', label: `Starts ${formatTime(task.startTime)}`, urgent: false }
    } else {
      return { status: 'past', label: 'Completed', urgent: false }
    }
  }

  const handleStatusClick = (task: Task, e: React.MouseEvent) => {
    e.stopPropagation()
    if (task.isAnchor) return
    
    let newStatus: TaskStatus
    switch (task.status) {
      case 'pending':
        newStatus = 'in_progress'
        break
      case 'in_progress':
        newStatus = 'complete'
        break
      case 'at_risk':
        newStatus = 'complete'
        break
      case 'delayed':
        newStatus = 'in_progress'
        break
      default:
        return
    }
    
    onTaskStatusChange?.(task.id, newStatus)
  }

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task.id)
    onTaskClick?.(task)
  }

  const handleDoubleClick = (task: Task, e: React.MouseEvent) => {
    e.preventDefault()
    console.log('Edit task:', task.name)
  }

  // Filter tasks by status and sort by start time
  const filteredAndSortedTasks = useMemo(() => {
    let filtered = [...tasks]
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter)
    }
    
    return filtered.sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
  }, [tasks, statusFilter])
  
  // Get status counts for filter buttons
  const statusCounts = useMemo(() => {
    const counts = {
      all: tasks.length,
      complete: 0,
      in_progress: 0,
      pending: 0,
      at_risk: 0,
      delayed: 0
    }
    
    tasks.forEach(task => {
      counts[task.status] = (counts[task.status] || 0) + 1
    })
    
    return counts
  }, [tasks])

  return (
    <div className="timeline-view-table">
      <div className="timeline-header">
        <div className="timeline-filters">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({statusCounts.all})
          </button>
          <button
            className={`filter-btn complete ${statusFilter === 'complete' ? 'active' : ''}`}
            onClick={() => setStatusFilter('complete')}
            disabled={statusCounts.complete === 0}
          >
            Complete ({statusCounts.complete})
          </button>
          <button
            className={`filter-btn in-progress ${statusFilter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setStatusFilter('in_progress')}
            disabled={statusCounts.in_progress === 0}
          >
            In Progress ({statusCounts.in_progress})
          </button>
          <button
            className={`filter-btn pending ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
            disabled={statusCounts.pending === 0}
          >
            Pending ({statusCounts.pending})
          </button>
          {statusCounts.at_risk > 0 && (
            <button
              className={`filter-btn at-risk ${statusFilter === 'at_risk' ? 'active' : ''}`}
              onClick={() => setStatusFilter('at_risk')}
            >
              At Risk ({statusCounts.at_risk})
            </button>
          )}
          {statusCounts.delayed > 0 && (
            <button
              className={`filter-btn delayed ${statusFilter === 'delayed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('delayed')}
            >
              Delayed ({statusCounts.delayed})
            </button>
          )}
        </div>
      </div>

      <div className="timeline-table">
        <div className="timeline-table-header">
          <div className="col-time">Time</div>
          <div className="col-activity">Activity</div>
          <div className="col-status">Status</div>
        </div>
        
        <div className="timeline-table-body">
          {filteredAndSortedTasks.length === 0 ? (
            <div className="timeline-empty">
              <div className="empty-icon">🔍</div>
              <div className="empty-message">
                No {statusFilter === 'all' ? 'tasks' : statusFilter.replace('_', ' ')} found
              </div>
            </div>
          ) : (
            filteredAndSortedTasks.map((task) => {
              const statusConfig = getStatusConfig(task.status)
              const timing = getTaskTiming(task)
              const isHovered = hoveredTask === task.id
              const isSelected = selectedTask === task.id
              
              return (
              <div
                key={task.id}
                className={`timeline-row status-${task.status} ${
                  timing.status === 'active' ? 'row-active' : ''
                } ${
                  timing.urgent ? 'row-urgent' : ''
                } ${
                  isHovered ? 'row-hovered' : ''
                } ${
                  isSelected ? 'row-selected' : ''
                } ${
                  task.isAnchor ? 'row-anchor' : ''
                }`}
                style={{
                  backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.05)' : undefined,
                  borderLeftColor: statusConfig.color
                }}
                onClick={() => handleTaskClick(task)}
                onDoubleClick={(e) => handleDoubleClick(task, e)}
                onMouseEnter={() => setHoveredTask(task.id)}
                onMouseLeave={() => setHoveredTask(null)}
              >
                <div className="col-time">
                  <div className="time-main">{formatTime(task.startTime)}</div>
                  <div className="time-duration">{formatDuration(task.duration)}</div>
                  {timing.status === 'active' && (
                    <div className="time-badge now-badge">NOW</div>
                  )}
                  {timing.status === 'soon' && (
                    <div className="time-badge soon-badge">SOON</div>
                  )}
                </div>
                
                <div className="col-activity">
                  <div className="activity-main">
                    {task.isAnchor && <span className="anchor-icon">🔒</span>}
                    <span className="activity-name">{task.name}</span>
                    {task.delayMinutes && task.delayMinutes > 0 && (
                      <span className="delay-badge">+{task.delayMinutes}m</span>
                    )}
                  </div>
                  <div className="activity-sub">
                    {task.supplier && (
                      <span className="supplier-name">{task.supplier.name}</span>
                    )}
                    {task.location && (
                      <span className="location-name">{task.location}</span>
                    )}
                  </div>
                </div>
                
                <div className="col-status">
                  <button
                    className={`status-chip status-${task.status} ${
                      task.isAnchor ? 'disabled' : ''
                    }`}
                    style={{
                      backgroundColor: statusConfig.color,
                      borderColor: statusConfig.color
                    }}
                    onClick={(e) => handleStatusClick(task, e)}
                    disabled={task.isAnchor || task.status === 'complete'}
                    title={statusConfig.action || statusConfig.label}
                  >
                    {statusConfig.label}
                  </button>
                  
                  {isHovered && !task.isAnchor && task.status !== 'complete' && (
                    <button 
                      className="at-risk-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        onTaskStatusChange?.(task.id, 'at_risk')
                      }}
                      title="Mark At Risk"
                    >
                      !
                    </button>
                  )}
                </div>
              </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

export default TimelineView